import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Award, Gift, Loader2, Plus, Search, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { loyaltyApi, posApi } from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';
import { getCurrencySymbol } from '@/utils/currency';
import { resolveFileUrl } from '@/utils/fileUrl';
import {
  LOYALTY_CARD_DESIGNS,
  type LoyaltyCardDesign,
} from '@/components/loyalty/LoyaltyMemberCard';
import LoyaltyCardDesignModal, {
  type LoyaltyCardDesignDraft,
} from '@/components/settings/LoyaltyCardDesignModal';

type ExclusionRow = {
  exclusion_type: string;
  category?: string;
  item?: string;
  label?: string;
  exclude_earn: number;
  exclude_redeem: number;
};

type CategoryOption = { name: string; category_name: string };
type ItemOption = { name: string; item_name?: string; item_code?: string; barcode?: string };

function ExclusionMatchField({
  row,
  categories,
  onChange,
}: {
  row: ExclusionRow;
  categories: CategoryOption[];
  onChange: (patch: Partial<ExclusionRow>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [itemResults, setItemResults] = useState<ItemOption[]>([]);
  const [itemLoading, setItemLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selectedCategory = categories.find((c) => c.name === row.category);
  const categoryDisplay = selectedCategory?.category_name || row.label || row.category || '';
  const itemDisplay = row.label || row.item || '';

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories.slice(0, 80);
    return categories
      .filter(
        (c) =>
          (c.category_name || '').toLowerCase().includes(q) ||
          (c.name || '').toLowerCase().includes(q),
      )
      .slice(0, 80);
  }, [categories, query]);

  useEffect(() => {
    if (row.exclusion_type !== 'Item' || !open) return;
    const q = query.trim();
    if (q.length < 1) {
      setItemResults([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      setItemLoading(true);
      try {
        const res = await posApi.getItems({ search: q, limit: 30 });
        const items = (res.data.message?.items || []) as ItemOption[];
        if (!cancelled) setItemResults(items);
      } catch {
        if (!cancelled) setItemResults([]);
      } finally {
        if (!cancelled) setItemLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query, open, row.exclusion_type]);

  if (row.exclusion_type === 'Keyword') {
    return (
      <input
        className="input-field text-sm w-full"
        placeholder="Keyword in product name…"
        value={row.label || ''}
        onChange={(e) => onChange({ label: e.target.value, category: '', item: '' })}
      />
    );
  }

  if (row.exclusion_type === 'Category') {
    return (
      <div className="relative w-full" ref={wrapRef}>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            className="input-field text-sm w-full pl-8 pr-8"
            placeholder="Search or select category…"
            value={open ? query : categoryDisplay}
            onFocus={() => {
              setOpen(true);
              setQuery(categoryDisplay);
            }}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
          />
          {(row.category || categoryDisplay) && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              onClick={() => {
                onChange({ category: '', label: '', item: '' });
                setQuery('');
              }}
              title="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {open && (
          <div className="absolute z-30 left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1">
            {filteredCategories.length === 0 ? (
              <p className="px-3 py-2 text-xs text-gray-400">No categories match</p>
            ) : (
              filteredCategories.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-primary-50 ${
                    c.name === row.category ? 'bg-primary-50 text-primary-800 font-medium' : 'text-gray-800'
                  }`}
                  onClick={() => {
                    onChange({ category: c.name, label: c.category_name, item: '' });
                    setQuery(c.category_name);
                    setOpen(false);
                  }}
                >
                  {c.category_name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    );
  }

  // Item / Product
  return (
    <div className="relative w-full" ref={wrapRef}>
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          className="input-field text-sm w-full pl-8 pr-8"
          placeholder="Search product by name or barcode…"
          value={open ? query : itemDisplay}
          onFocus={() => {
            setOpen(true);
            setQuery(itemDisplay);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
        {(row.item || itemDisplay) && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={() => {
              onChange({ item: '', label: '', category: '' });
              setQuery('');
              setItemResults([]);
            }}
            title="Clear"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1">
          {itemLoading ? (
            <p className="px-3 py-2 text-xs text-gray-400 inline-flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Searching…
            </p>
          ) : query.trim().length < 1 ? (
            <p className="px-3 py-2 text-xs text-gray-400">Type a name or barcode to search</p>
          ) : itemResults.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-400">No products found</p>
          ) : (
            itemResults.map((it) => (
              <button
                key={it.name}
                type="button"
                className={`w-full text-left px-3 py-2 text-xs hover:bg-primary-50 border-b border-gray-50 last:border-0 ${
                  it.name === row.item ? 'bg-primary-50' : ''
                }`}
                onClick={() => {
                  const label = it.item_name || it.item_code || it.name;
                  onChange({ item: it.name, label, category: '' });
                  setQuery(label);
                  setOpen(false);
                }}
              >
                <span className="font-medium text-gray-900 block">{it.item_name || it.name}</span>
                <span className="text-[11px] text-gray-400">
                  {it.item_code || it.name}
                  {it.barcode ? ` · ${it.barcode}` : ''}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

type OverrideRow = {
  item: string;
  item_name?: string;
  earn_mode: string;
  fixed_points: number;
  multiplier: number;
  is_alcohol: number;
  no_bonus_on_alcohol: number;
};

type TierRow = {
  tier_name: string;
  min_points: string;
  badge_color: string;
};

type FormState = {
  is_active: boolean;
  earn_rate: string;
  earn_basis: string;
  earn_rounding: string;
  earn_on_points_paid: boolean;
  earn_on_credit: string;
  redeem_rate: string;
  min_redeem_points: string;
  min_basket_to_redeem: string;
  max_basket_percent: string;
  redeem_increment: string;
  eligible_retailer: boolean;
  eligible_wholesale: boolean;
  eligible_distributor: boolean;
  eligible_registered: boolean;
  expiry_mode: string;
  expiry_months: string;
  refund_redeem_policy: string;
  negative_balance_policy: string;
  cashier_can_redeem: boolean;
  manager_can_adjust: boolean;
  cashier_can_override_exclusions: boolean;
  terms_html: string;
  no_bonus_on_alcohol: boolean;
  tiers: TierRow[];
  card_design: LoyaltyCardDesign;
  card_colour: string;
  card_logo: string;
  card_tagline: string;
  card_show_points: boolean;
  card_show_value: boolean;
  card_show_tier: boolean;
  exclusions: ExclusionRow[];
  item_overrides: OverrideRow[];
};

const MAX_TIERS = 5;

const emptyForm = (): FormState => ({
  is_active: false,
  earn_rate: '1',
  earn_basis: 'Incl VAT',
  earn_rounding: 'Round Down',
  earn_on_points_paid: false,
  earn_on_credit: 'Invoice',
  redeem_rate: '0.01',
  min_redeem_points: '100',
  min_basket_to_redeem: '0',
  max_basket_percent: '100',
  redeem_increment: '1',
  eligible_retailer: true,
  eligible_wholesale: true,
  eligible_distributor: true,
  eligible_registered: true,
  expiry_mode: 'Never',
  expiry_months: '12',
  refund_redeem_policy: 'Restore Points',
  negative_balance_policy: 'Cap at Zero',
  cashier_can_redeem: true,
  manager_can_adjust: true,
  cashier_can_override_exclusions: false,
  terms_html: '',
  no_bonus_on_alcohol: false,
  tiers: [],
  card_design: 'Fresh',
  card_colour: '#15803d',
  card_logo: '',
  card_tagline: '',
  card_show_points: true,
  card_show_value: true,
  card_show_tier: true,
  exclusions: [],
  item_overrides: [],
});

export default function LoyaltySettingsTab() {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [showCardDesign, setShowCardDesign] = useState(false);
  const sym = getCurrencySymbol();

  const { data, isLoading } = useQuery({
    queryKey: ['loyaltySettings'],
    queryFn: async () => (await loyaltyApi.getSettings()).data.message,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['loyaltyExclusionCategories'],
    queryFn: async () => {
      const res = await posApi.getCategories();
      return (res.data.message || []) as CategoryOption[];
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      is_active: Boolean(data.is_active ?? data.enabled),
      earn_rate: String(data.earn_rate ?? 1),
      earn_basis: data.earn_basis || 'Incl VAT',
      earn_rounding: data.earn_rounding || 'Round Down',
      earn_on_points_paid: Boolean(data.earn_on_points_paid),
      earn_on_credit: data.earn_on_credit || 'Invoice',
      redeem_rate: String(data.redeem_rate ?? 0.01),
      min_redeem_points: String(data.min_redeem_points ?? 0),
      min_basket_to_redeem: String(data.min_basket_to_redeem ?? 0),
      max_basket_percent: String(data.max_basket_percent ?? 100),
      redeem_increment: String(data.redeem_increment ?? 1),
      eligible_retailer: data.eligible_retailer !== false,
      eligible_wholesale: data.eligible_wholesale !== false,
      eligible_distributor: data.eligible_distributor !== false,
      eligible_registered: data.eligible_registered !== false,
      expiry_mode: data.expiry_mode || 'Never',
      expiry_months: String(data.expiry_months ?? 12),
      refund_redeem_policy: data.refund_redeem_policy || 'Restore Points',
      negative_balance_policy: data.negative_balance_policy || 'Cap at Zero',
      cashier_can_redeem: data.cashier_can_redeem !== false,
      manager_can_adjust: data.manager_can_adjust !== false,
      cashier_can_override_exclusions: Boolean(data.cashier_can_override_exclusions),
      terms_html: data.terms_html || '',
      no_bonus_on_alcohol: Boolean(data.no_bonus_on_alcohol),
      tiers: (data.tiers || []).map((r: { tier_name?: string; min_points?: number; badge_color?: string }) => ({
        tier_name: r.tier_name || '',
        min_points: String(r.min_points ?? 0),
        badge_color: r.badge_color || '',
      })),
      card_design: (['Fresh', 'Tech', 'Style'].includes(data.card_design)
        ? data.card_design
        : 'Fresh') as LoyaltyCardDesign,
      card_colour: data.card_colour || '#15803d',
      // Override only — empty means use company logo on the card
      card_logo: data.card_logo || '',
      card_tagline: data.card_tagline || '',
      card_show_points: data.card_show_points !== false && data.card_show_points !== 0,
      card_show_value: data.card_show_value !== false && data.card_show_value !== 0,
      card_show_tier: data.card_show_tier !== false && data.card_show_tier !== 0,
      exclusions: (data.exclusions || []).map((r: ExclusionRow) => ({ ...r })),
      item_overrides: (data.item_overrides || []).map((r: OverrideRow) => ({ ...r })),
    });
  }, [data]);

  // Fill category display names once category list is available (does not reset other edits).
  useEffect(() => {
    if (!categories.length) return;
    setForm((f) => {
      let changed = false;
      const exclusions = f.exclusions.map((row) => {
        if (row.exclusion_type !== 'Category' || !row.category || row.label) return row;
        const cat = categories.find((c) => c.name === row.category);
        if (!cat) return row;
        changed = true;
        return { ...row, label: cat.category_name };
      });
      return changed ? { ...f, exclusions } : f;
    });
  }, [categories]);

  const effectivePct = useMemo(() => {
    const earn = parseFloat(form.earn_rate) || 0;
    const redeem = parseFloat(form.redeem_rate) || 0;
    return (earn * redeem * 100).toFixed(2);
  }, [form.earn_rate, form.redeem_rate]);

  const buildSavePayload = (overrides: Partial<FormState> = {}) => {
    const merged = { ...form, ...overrides };
    const tiers = merged.tiers
      .filter((t) => (t.tier_name || '').trim())
      .map((t) => ({
        tier_name: t.tier_name.trim(),
        min_points: parseFloat(t.min_points) || 0,
        badge_color: (t.badge_color || '').trim(),
      }));
    if (tiers.length > MAX_TIERS) {
      throw new Error(`Maximum ${MAX_TIERS} tiers allowed`);
    }
    return {
      ...merged,
      program_name: 'Loyalty',
      earn_rate: parseFloat(merged.earn_rate) || 0,
      redeem_rate: parseFloat(merged.redeem_rate) || 0,
      min_redeem_points: parseFloat(merged.min_redeem_points) || 0,
      min_basket_to_redeem: parseFloat(merged.min_basket_to_redeem) || 0,
      max_basket_percent: parseFloat(merged.max_basket_percent) || 0,
      redeem_increment: parseFloat(merged.redeem_increment) || 1,
      expiry_months: parseInt(merged.expiry_months, 10) || 12,
      tiers,
    };
  };

  const saveMutation = useMutation({
    mutationFn: async () => (await loyaltyApi.saveProgram(buildSavePayload())).data.message,
    onSuccess: () => {
      toast.success('Loyalty settings saved');
      qc.invalidateQueries({ queryKey: ['loyaltySettings'] });
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to save')),
  });

  const saveCardMutation = useMutation({
    mutationFn: async (draft: LoyaltyCardDesignDraft) => {
      const next = {
        card_design: draft.card_design,
        card_colour: draft.card_colour,
        card_logo: draft.card_logo,
        card_tagline: draft.card_tagline,
        card_show_points: draft.card_show_points,
        card_show_value: draft.card_show_value,
        card_show_tier: draft.card_show_tier,
      };
      setForm((f) => ({ ...f, ...next }));
      return (await loyaltyApi.saveProgram(buildSavePayload(next))).data.message;
    },
    onSuccess: () => {
      toast.success('Card design saved');
      qc.invalidateQueries({ queryKey: ['loyaltySettings'] });
      setShowCardDesign(false);
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Failed to save card design')),
  });

  const seedDefaults = () => {
    const keywords: string[] = data?.default_exclusion_keywords || [];
    const existing = new Set(form.exclusions.map((e) => (e.label || '').toLowerCase()));
    const added = keywords
      .filter((k) => !existing.has(k.toLowerCase()))
      .map(
        (k): ExclusionRow => ({
          exclusion_type: 'Keyword',
          label: k,
          exclude_earn: 1,
          exclude_redeem: 0,
        }),
      );
    setForm((f) => ({ ...f, exclusions: [...f.exclusions, ...added] }));
  };

  const seedSuggestedTiers = () => {
    const defaults: TierRow[] = (data?.default_tiers || [
      { tier_name: 'Silver', min_points: 500, badge_color: '#94a3b8' },
      { tier_name: 'Gold', min_points: 2000, badge_color: '#d4a017' },
      { tier_name: 'Platinum', min_points: 5000, badge_color: '#64748b' },
    ]).map((t: { tier_name: string; min_points: number; badge_color?: string }) => ({
      tier_name: t.tier_name,
      min_points: String(t.min_points),
      badge_color: t.badge_color || '',
    }));
    setForm((f) => ({ ...f, tiers: defaults.slice(0, MAX_TIERS) }));
  };

  if (isLoading) {
    return (
      <div className="card p-8 flex items-center justify-center text-gray-400 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading loyalty settings…
      </div>
    );
  }

  return (
    <div className="card p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary-600" />
          Loyalty programme
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure earn/redeem rules and customer tiers. When off, no points are earned or redeemed and the
          portal card is hidden. Existing balances are kept.
        </p>
      </div>

      {/* Prominent master switch — one program per shop, no name required */}
      <button
        type="button"
        role="switch"
        aria-checked={form.is_active}
        onClick={() => setForm({ ...form, is_active: !form.is_active })}
        className={`w-full flex items-center justify-between gap-4 rounded-xl border-2 px-5 py-4 text-left transition-colors ${
          form.is_active
            ? 'border-emerald-400 bg-emerald-50 shadow-sm'
            : 'border-gray-200 bg-gray-50 hover:border-gray-300'
        }`}
      >
        <div className="min-w-0">
          <div className="text-base font-semibold text-gray-900">
            {form.is_active ? 'Loyalty is on' : 'Enable loyalty'}
          </div>
          <p className="text-sm text-gray-600 mt-0.5">
            {form.is_active
              ? 'Customers can earn and redeem points. Turn off anytime — balances stay.'
              : 'Turn on to start earning and redeeming points for enrolled customers.'}
          </p>
        </div>
        <span
          className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${
            form.is_active ? 'bg-emerald-500' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
              form.is_active ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </span>
      </button>

      <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
        Effective cash-back ≈ <strong>{effectivePct}%</strong> (earn rate × point value)
      </div>

      <section className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between gap-3 flex-wrap rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-lg shrink-0 shadow-sm border border-white"
              style={{
                background: `linear-gradient(135deg, ${form.card_colour || '#15803d'}, ${
                  form.card_colour || '#15803d'
                }99)`,
              }}
            />
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900">Digital card</h3>
              <p className="text-xs text-gray-500 truncate">
                {LOYALTY_CARD_DESIGNS.find((d) => d.id === form.card_design)?.label || 'Fresh'}
                {form.card_colour ? ` · ${form.card_colour}` : ''}
                {form.card_tagline ? ` · ${form.card_tagline}` : ''}
                {form.card_logo ? ' · custom logo' : ' · company logo'}
              </p>
            </div>
            {(form.card_logo || data?.company_logo || data?.card_logo_effective) && (
              <img
                src={resolveFileUrl(
                  form.card_logo || data?.company_logo || data?.card_logo_effective || '',
                )}
                alt=""
                className="w-9 h-9 rounded-lg border border-gray-200 object-contain bg-white shrink-0"
              />
            )}
          </div>
          <button
            type="button"
            className="btn-secondary text-sm shrink-0"
            onClick={() => setShowCardDesign(true)}
          >
            Design card
          </button>
        </div>
      </section>

      <LoyaltyCardDesignModal
        open={showCardDesign}
        onClose={() => {
          if (!saveCardMutation.isPending) setShowCardDesign(false);
        }}
        initial={{
          card_design: form.card_design,
          card_colour: form.card_colour,
          card_logo: form.card_logo,
          card_tagline: form.card_tagline,
          card_show_points: form.card_show_points,
          card_show_value: form.card_show_value,
          card_show_tier: form.card_show_tier,
        }}
        shopName={data?.shop_name || 'Your Shop'}
        companyLogo={data?.company_logo || ''}
        tiers={form.tiers
          .filter((t) => (t.tier_name || '').trim())
          .map((t) => ({ tier_name: t.tier_name, badge_color: t.badge_color }))}
        saving={saveCardMutation.isPending}
        onSave={(draft) => saveCardMutation.mutate(draft)}
      />

      <section className="space-y-3 border-t border-gray-100 pt-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
              <Award className="w-3.5 h-3.5" /> Tiers
            </h3>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              Badge customers by lifetime points earned (not current balance). Design up to {MAX_TIERS}{' '}
              tiers — e.g. Silver, Gold, Platinum — each with a points threshold.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {form.tiers.length === 0 && (
              <button type="button" className="btn-secondary text-xs py-1.5" onClick={seedSuggestedTiers}>
                Add Silver / Gold / Platinum
              </button>
            )}
            <button
              type="button"
              className="btn-secondary text-xs py-1.5 inline-flex items-center gap-1"
              disabled={form.tiers.length >= MAX_TIERS}
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  tiers: [
                    ...f.tiers,
                    { tier_name: '', min_points: '0', badge_color: '' },
                  ],
                }))
              }
            >
              <Plus className="w-3.5 h-3.5" /> Add tier
            </button>
          </div>
        </div>

        {form.tiers.length === 0 ? (
          <p className="text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-4">
            No tiers yet — customers still earn points, but no badge levels are shown.
          </p>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Tier name</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600 w-40">
                    Min points earned
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600 w-28">Colour</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {form.tiers.map((tier, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2">
                      <input
                        className="input-field text-sm py-1.5"
                        placeholder="e.g. Gold"
                        value={tier.tier_name}
                        onChange={(e) => {
                          const tiers = [...form.tiers];
                          tiers[idx] = { ...tier, tier_name: e.target.value };
                          setForm({ ...form, tiers });
                        }}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min="0"
                        className="input-field text-sm py-1.5"
                        value={tier.min_points}
                        onChange={(e) => {
                          const tiers = [...form.tiers];
                          tiers[idx] = { ...tier, min_points: e.target.value };
                          setForm({ ...form, tiers });
                        }}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          className="h-8 w-8 rounded border border-gray-200 cursor-pointer bg-white p-0.5"
                          value={
                            tier.badge_color && /^#[0-9A-Fa-f]{6}$/.test(tier.badge_color)
                              ? tier.badge_color
                              : '#94a3b8'
                          }
                          onChange={(e) => {
                            const tiers = [...form.tiers];
                            tiers[idx] = { ...tier, badge_color: e.target.value };
                            setForm({ ...form, tiers });
                          }}
                          title="Badge colour"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            tiers: f.tiers.filter((_, i) => i !== idx),
                          }))
                        }
                        title="Remove tier"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-gray-400">
          {form.tiers.length}/{MAX_TIERS} tiers · Highest matching threshold wins
        </p>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Earn</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Points per {sym} spent</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="input-field"
              value={form.earn_rate}
              onChange={(e) => setForm({ ...form, earn_rate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Basis</label>
            <select
              className="input-field"
              value={form.earn_basis}
              onChange={(e) => setForm({ ...form, earn_basis: e.target.value })}
            >
              <option value="Incl VAT">Amount paid incl. VAT</option>
              <option value="Excl VAT">Amount excl. VAT</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rounding</label>
            <select
              className="input-field"
              value={form.earn_rounding}
              onChange={(e) => setForm({ ...form, earn_rounding: e.target.value })}
            >
              <option value="Round Down">Round down</option>
              <option value="Pro-rata">Pro-rata</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Credit sales earn on</label>
            <select
              className="input-field"
              value={form.earn_on_credit}
              onChange={(e) => setForm({ ...form, earn_on_credit: e.target.value })}
            >
              <option value="Invoice">Invoice</option>
              <option value="Payment">Payment</option>
            </select>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.earn_on_points_paid}
            onChange={(e) => setForm({ ...form, earn_on_points_paid: e.target.checked })}
          />
          Earn on portion paid with points
        </label>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Redeem</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{sym} value per point</label>
            <input
              type="number"
              min="0"
              step="0.0001"
              className="input-field"
              value={form.redeem_rate}
              onChange={(e) => setForm({ ...form, redeem_rate: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min points to redeem</label>
            <input
              type="number"
              min="0"
              className="input-field"
              value={form.min_redeem_points}
              onChange={(e) => setForm({ ...form, min_redeem_points: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min basket ({sym})</label>
            <input
              type="number"
              min="0"
              className="input-field"
              value={form.min_basket_to_redeem}
              onChange={(e) => setForm({ ...form, min_basket_to_redeem: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max % of basket</label>
            <input
              type="number"
              min="0"
              max="100"
              className="input-field"
              value={form.max_basket_percent}
              onChange={(e) => setForm({ ...form, max_basket_percent: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Redeem in multiples of</label>
            <input
              type="number"
              min="1"
              className="input-field"
              value={form.redeem_increment}
              onChange={(e) => setForm({ ...form, redeem_increment: e.target.value })}
            />
          </div>
        </div>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Eligibility</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          {(
            [
              ['eligible_retailer', 'Retailer'],
              ['eligible_wholesale', 'Wholesale'],
              ['eligible_distributor', 'Distributor'],
              ['eligible_registered', 'Registered (non walk-in)'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
              />
              {label}
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500">Walk-in customers never earn or redeem.</p>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Expiry</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
            <select
              className="input-field"
              value={form.expiry_mode}
              onChange={(e) => setForm({ ...form, expiry_mode: e.target.value })}
            >
              <option value="Never">Never</option>
              <option value="Months After Activity">Months after last earn/redeem</option>
            </select>
          </div>
          {form.expiry_mode !== 'Never' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Months</label>
              <input
                type="number"
                min="1"
                className="input-field"
                value={form.expiry_months}
                onChange={(e) => setForm({ ...form, expiry_months: e.target.value })}
              />
            </div>
          )}
        </div>
        {form.expiry_mode !== 'Never' && (
          <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            Changing expiry applies going forward. Notify customers before tightening the window.
          </div>
        )}
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Refunds & permissions</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Refund of redeemed points</label>
            <select
              className="input-field"
              value={form.refund_redeem_policy}
              onChange={(e) => setForm({ ...form, refund_redeem_policy: e.target.value })}
            >
              <option value="Restore Points">Restore points</option>
              <option value="Refund as Cash">Refund as cash</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              On a return: put redeemed points back on the card, or refund that value as cash instead.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Negative balance</label>
            <select
              className="input-field"
              value={form.negative_balance_policy}
              onChange={(e) => setForm({ ...form, negative_balance_policy: e.target.value })}
            >
              <option value="Cap at Zero">Cap at zero</option>
              <option value="Allow Negative">Allow negative</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              If a return reverses more points than left on the card, stop at 0 or allow a negative balance.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="inline-flex items-center gap-2" title="Till staff may apply points at checkout">
            <input
              type="checkbox"
              checked={form.cashier_can_redeem}
              onChange={(e) => setForm({ ...form, cashier_can_redeem: e.target.checked })}
            />
            Cashier can redeem
          </label>
          <label className="inline-flex items-center gap-2" title="Manager/admin may manually add or remove points">
            <input
              type="checkbox"
              checked={form.manager_can_adjust}
              onChange={(e) => setForm({ ...form, manager_can_adjust: e.target.checked })}
            />
            Manager can adjust
          </label>
          <label className="inline-flex items-center gap-2" title="Allow cashier to ignore product exclusion rules (normally leave off)">
            <input
              type="checkbox"
              checked={form.cashier_can_override_exclusions}
              onChange={(e) => setForm({ ...form, cashier_can_override_exclusions: e.target.checked })}
            />
            Cashier can override exclusions
          </label>
          <label className="inline-flex items-center gap-2" title="Alcohol lines do not earn points">
            <input
              type="checkbox"
              checked={form.no_bonus_on_alcohol}
              onChange={(e) => setForm({ ...form, no_bonus_on_alcohol: e.target.checked })}
            />
            No bonus points on alcohol
          </label>
        </div>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Exclusions</h3>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary text-xs" onClick={seedDefaults}>
              Seed UK defaults
            </button>
            <button
              type="button"
              className="btn-secondary text-xs inline-flex items-center gap-1"
              onClick={() =>
                setForm({
                  ...form,
                  exclusions: [
                    ...form.exclusions,
                    { exclusion_type: 'Keyword', label: '', exclude_earn: 1, exclude_redeem: 0 },
                  ],
                })
              }
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Block specific products from loyalty. Use a keyword in the product name, pick a category from the list,
          or search a product by name/barcode. Tick a box to{' '}
          <span className="font-medium text-gray-700">exclude</span> that action for the match.
        </p>
        <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600 space-y-1">
          <p>
            <span className="font-semibold text-gray-800">Earn</span> — ticked means these products
            <span className="font-medium"> do not earn</span> points when sold.
          </p>
          <p>
            <span className="font-semibold text-gray-800">Redeem</span> — ticked means points
            <span className="font-medium"> cannot be used</span> toward these products (or a basket that includes them, depending on rules).
          </p>
          <p className="text-gray-500">
            Example: Earn on, Redeem off → tobacco/lottery never earn points, but a customer can still redeem points on other lines in the same sale.
          </p>
        </div>
        <div className="hidden sm:grid grid-cols-12 gap-2 px-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
          <span className="col-span-3">Type</span>
          <span className="col-span-5">Match</span>
          <span className="col-span-1" title="Exclude from earning points">Earn</span>
          <span className="col-span-1" title="Exclude from redeeming points">Redeem</span>
          <span className="col-span-2" />
        </div>
        <div className="space-y-2">
          {form.exclusions.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center">
              <select
                className="input-field col-span-3 text-sm"
                value={row.exclusion_type}
                onChange={(e) => {
                  const exclusions = [...form.exclusions];
                  exclusions[idx] = {
                    ...row,
                    exclusion_type: e.target.value,
                    category: '',
                    item: '',
                    label: '',
                  };
                  setForm({ ...form, exclusions });
                }}
              >
                <option value="Keyword">Keyword</option>
                <option value="Category">Category</option>
                <option value="Item">Product</option>
              </select>
              <div className="col-span-5 min-w-0">
                <ExclusionMatchField
                  row={row}
                  categories={categories}
                  onChange={(patch) => {
                    const exclusions = [...form.exclusions];
                    exclusions[idx] = { ...row, ...patch };
                    setForm({ ...form, exclusions });
                  }}
                />
              </div>
              <label
                className="col-span-1 text-xs flex items-center gap-1"
                title="When ticked: matching products do not earn points"
              >
                <input
                  type="checkbox"
                  checked={!!row.exclude_earn}
                  onChange={(e) => {
                    const exclusions = [...form.exclusions];
                    exclusions[idx] = { ...row, exclude_earn: e.target.checked ? 1 : 0 };
                    setForm({ ...form, exclusions });
                  }}
                />
                Earn
              </label>
              <label
                className="col-span-1 text-xs flex items-center gap-1"
                title="When ticked: points cannot be redeemed against matching products"
              >
                <input
                  type="checkbox"
                  checked={!!row.exclude_redeem}
                  onChange={(e) => {
                    const exclusions = [...form.exclusions];
                    exclusions[idx] = { ...row, exclude_redeem: e.target.checked ? 1 : 0 };
                    setForm({ ...form, exclusions });
                  }}
                />
                Redeem
              </label>
              <button
                type="button"
                className="col-span-2 text-red-600 text-xs inline-flex items-center gap-1"
                onClick={() =>
                  setForm({ ...form, exclusions: form.exclusions.filter((_, i) => i !== idx) })
                }
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
          ))}
          {form.exclusions.length === 0 && (
            <p className="text-sm text-gray-400">No exclusions yet.</p>
          )}
        </div>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">Terms &amp; conditions</h3>
        <p className="text-xs text-gray-500">
          Shown to customers when they join loyalty in the online account / portal.
        </p>
        <textarea
          className="input-field min-h-[120px]"
          placeholder="Loyalty T&Cs shown in the portal when joining…"
          value={form.terms_html}
          onChange={(e) => setForm({ ...form, terms_html: e.target.value })}
        />
      </section>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          className="btn-primary inline-flex items-center gap-2"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
        >
          {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Save loyalty settings
        </button>
      </div>
    </div>
  );
}
